import Vue from 'vue';
import Vuex from 'vuex';
import auth from './auth';
import voting from './voting';
import changeRequests from './change-requests';
import RegistrationService from '../services/RegistrationsService';
import RefferencesService from '../services/RefferencesService';
import Constants from '../services/Constants';
import FilesService from '../services/FilesService';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    registration: {},
    steps: [],
    plans: {},
    lookup: {
      plans: [],
    },
  },
  getters: {
    currentStepIndex(state) {
      return state.registration?.currentStepIndex;
    },
    validStepIndexes(state) {
      return state.registration?.validStepIndexes;
    },
    plansCount(state) {
      return Object.keys(state.plans).length;
    },
    areaOfOperations() {
      return Constants.areaOfOperations.map((item) => {
        return {
          value: item,
          icon: 'uil:house-user',
        };
      });
    },
    trainingOrganisations() {
      return Constants.trainingOrganisations;
    },
    howDidYouHearUs() {
      return Constants.howDidYouHearUs;
    },
    insurers() {
      return Constants.insurers;
    },
  },
  mutations: {
    setRegistration(state, registration) {
      state.registration = registration;
    },
    setRegistrationField(state, { objectKey, fieldKey, value }) {
      if (objectKey) {
        if (fieldKey) {
          Vue.set(state.registration[objectKey], fieldKey, value);
        } else {
          Vue.set(state.registration, objectKey, value);
        }
      }
    },
    setPlans(state, plans) {
      state.lookup.plans = plans;
      state.plans = {};

      // filter out plans first
      const _plans = plans.filter((p) => !p.is2YearsPlan);
      for (const plan of _plans) {
        const plans = state.plans[plan.type] || [];
        plans.push(plan);
        Vue.set(state.plans, plan.type, plans);
      }
    },
    setSteps(state, steps) {
      state.steps = steps;
    },
    setCurrentStepIndex(state, stepIndex) {
      Vue.set(state.registration, 'currentStepIndex', stepIndex);
    },
    addValidStepIndex(state, stepIndex) {
      if (!state.registration.validStepIndexes) {
        state.registration.validStepIndexes = [stepIndex];
      } else if (!state.registration.validStepIndexes.includes(stepIndex)) {
        state.registration.validStepIndexes.push(stepIndex);
      }
    },
    removeValidStepIndex(state, stepIndex) {
      let position = state.registration.validStepIndexes?.indexOf(stepIndex);
      if (position > -1) {
        state.registration.validStepIndexes.splice(position, 1);
      }
    },
  },
  actions: {
    clearClientState({ dispatch }) {
      return new Promise((resolve) => {
        dispatch('clearRegistration');
        dispatch('auth/doLogout');
        dispatch('voting/initialize');
        dispatch('changeRequests/initialize');

        localStorage.removeItem('vuex.state.registration');
        localStorage.removeItem('vuex.state.authUser');
        sessionStorage.removeItem('vuex.session.authUser');

        resolve();
      });
    },
    initialize({ commit, state }) {
      //reffs
      RefferencesService.getPlans()
        .then((plans) => {
          commit('setPlans', plans);
        })
        .catch((error) => {
          console.error('Failed to load plans', error);
          commit('setPlans', []);
        });
      //cached registration
      if (!state.registration || !state.registration.id) {
        const registrationJson = localStorage.getItem('vuex.state.registration');
        if (registrationJson && registrationJson.length > 0) {
          const registration = JSON.parse(registrationJson);
          commit('setRegistration', registration);
        }
      }
    },
    initRegistration({ commit }, type) {
      return new Promise((resolve) => {
        const newRegistration = {
          type,
        };
        commit('setRegistration', newRegistration);
        resolve(newRegistration);
        // RegistrationService.createRegistration(newRegistration).then(
        //   (registration) => {
        //     commit("setRegistration", registration);
        //     resolve(registration);
        //   }
        // );
      });
    },
    updateRegistration({ commit }, registration) {
      Object.keys(registration).forEach((key) => {
        commit('setRegistrationField', {
          objectKey: key,
          value: registration[key],
        });
      });
    },
    saveRegistration({ state, commit }) {
      return new Promise((resolve) => {
        if (!state.registration.id) {
          RegistrationService.createRegistration(state.registration).then((registration) => {
            commit('setRegistration', registration);
            resolve(registration);
          });
        } else {
          RegistrationService.updateRegistration(state.registration).then((registration) => {
            resolve(registration);
          });
        }
      });
    },
    evaluateRegistrationPlan({ state, commit }) {
      if (state.registration.type) {
        const get2YearsPlan = (_plan) => {
          const _foundPlan = state.lookup.plans.find((p) => p.is2YearsPlan && p.relatedPlanID === _plan.id);
          return _foundPlan;
        };

        if (state.registration.type === 'plumber') {
          let area = 'metro'; // default
          const postcode = parseInt(state.registration.company?.physicalAddress?.postCode || 6000);
          const matchedArea = Constants.areas.find((a) => a.postcodeRanges.find((pc) => pc.min <= postcode && pc.max >= postcode));
          if (matchedArea) {
            area = matchedArea.name;
          }
          const totalEmployees = parseInt(state.registration.employees?.total || 1);
          const plumberPlans = state.plans.plumber || [];
          const matchedPlan = plumberPlans.find((plan) => {
            return (
              plan.area === area &&
              (!plan.minEmployees || plan.minEmployees <= totalEmployees) &&
              (!plan.maxEmployees || plan.maxEmployees >= totalEmployees)
            );
          });
          if (matchedPlan) {
            const _matchedPlan = state.registration.payment?.pay2YearsUpfront ? get2YearsPlan(matchedPlan) : matchedPlan;
            commit('setRegistrationField', {
              objectKey: 'plan',
              value: _matchedPlan,
            });
          } else {
            commit('setRegistrationField', {
              objectKey: 'plan',
              value: null,
            });
          }
        } else {
          const typePlans = state.plans[state.registration.type] || [];
          if (typePlans.length > 0) {
            const matchedPlan = typePlans[0];
            const _matchedPlan = state.registration.payment?.pay2YearsUpfront ? get2YearsPlan(matchedPlan) : matchedPlan;
            commit('setRegistrationField', {
              objectKey: 'plan',
              value: _matchedPlan,
            });
          } else {
            commit('setRegistrationField', {
              objectKey: 'plan',
              value: null,
            });
          }
        }
      }
    },
    saveAndNotifyRegistration({ state, dispatch }) {
      return new Promise((resolve) => {
        dispatch('saveRegistration').then(() => {
          RegistrationService.saveRegistration(state.registration.id).then((registration) => {
            resolve(registration);
          });
        });
      });
    },
    retrieveRegistration({ commit }, { number, email }) {
      return new Promise((resolve) => {
        RegistrationService.retrieveRegistration(number, email)
          .then((registration) => {
            if (registration) {
              commit('setRegistration', registration);
              resolve(registration);
            } else {
              resolve(null);
            }
          })
          .catch((error) => {
            console.error('error retrieving registration', error);
            resolve(null);
          });
      });
    },
    submitRegistration({ state }, payment) {
      return new Promise((resolve, reject) => {
        RegistrationService.submitRegistration(state.registration.id, payment)
          .then((registration) => {
            resolve(registration);
          })
          .catch((error) => {
            if (error.response?.data) {
              reject(error.response.data);
            } else {
              reject(error);
            }
          });
      });
    },
    clearRegistration({ commit }) {
      commit('setRegistration', {});
    },
    uploadFile({ state }, { file, fileType, onUploadProgress }) {
      return FilesService.uploadFile(file, state.registration.id, fileType, onUploadProgress);
    },
    setSteps({ commit }, steps) {
      commit('setSteps', steps);
    },
    setCurrentStep({ commit, state }, { currentPath }) {
      const currentStepIndex = state.steps.findIndex((s) => s.path === currentPath);
      commit('setCurrentStepIndex', currentStepIndex);
    },
    nextStep({ commit, state }, { currentPath, isStepValid }) {
      return new Promise((resolve) => {
        const currentStepIndex = state.steps.findIndex((s) => s.path === currentPath);
        if (currentStepIndex > -1) {
          if (isStepValid) {
            commit('addValidStepIndex', currentStepIndex);
          } else {
            commit('removeValidStepIndex', currentStepIndex);
          }
          const nextStepIndex = currentStepIndex + 1;
          if (state.steps.length > nextStepIndex) {
            resolve(state.steps[nextStepIndex].path);
          }
        }
        resolve(currentPath);
      });
    },
    prevStep({ commit, state }, { currentPath, isStepValid }) {
      return new Promise((resolve) => {
        const currentStepIndex = state.steps.findIndex((s) => s.path === currentPath);
        if (currentStepIndex > -1) {
          if (isStepValid) {
            commit('addValidStepIndex', currentStepIndex);
          } else {
            commit('removeValidStepIndex', currentStepIndex);
          }
          const prevStepIndex = currentStepIndex - 1;
          if (prevStepIndex > -1 && state.steps.length > prevStepIndex) {
            commit('setCurrentStepIndex', prevStepIndex);
            resolve(state.steps[prevStepIndex].path);
          }
        }
        resolve(currentPath);
      });
    },
  },
  modules: {
    auth,
    voting,
    changeRequests,
  },
});
